import { useRef, useState } from 'react'

import { cn } from '@/app/utils'
import { useOutsideClick } from '@/hooks/useOutsideClick'

import { EMOJI_CATEGORIES } from '../model/emoji'

type Props = {
  value?: string | null
  onChange: (emoji: string) => void
  className?: string
  dataTestId?: string
}

const EmojiPicker = ({ value, onChange, className, dataTestId }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeCategoryId, setActiveCategoryId] = useState(
    EMOJI_CATEGORIES[0].id
  )

  const wrapperRef = useRef<HTMLDivElement>(null)

  useOutsideClick({
    ref: wrapperRef,
    isOpen,
    onClose: () => setIsOpen(false)
  })

  const activeCategory =
    EMOJI_CATEGORIES.find(category => category.id === activeCategoryId) ??
    EMOJI_CATEGORIES[0]

  const handleEmojiClick = (emoji: string) => {
    onChange(emoji)
    setIsOpen(false)
  }

  return (
    <div ref={wrapperRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        data-testid={dataTestId}
        onClick={() => setIsOpen(prev => !prev)}
        className="flex h-56 w-56 items-center justify-center rounded-12 border-none bg-white text-24 sm:h-48 sm:w-48"
      >
        {value || '🙂'}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-[64px] z-20 w-[336px] rounded-16 border border-grey-20 bg-white p-16 shadow-[0px_7px_10px_0px_rgba(22,23,24,0.10)] sm:top-[56px]">
          <div className="mb-12 flex items-center justify-between border-b border-grey-20 pb-8">
            {EMOJI_CATEGORIES.map(category => (
              <button
                key={category.id}
                type="button"
                title={category.name}
                aria-label={category.name}
                onClick={() => setActiveCategoryId(category.id)}
                className={cn(
                  'flex h-40 w-40 items-center justify-center rounded-8 text-20',
                  category.id === activeCategoryId
                    ? 'bg-grey-10 opacity-100'
                    : 'opacity-40 hover:opacity-100'
                )}
              >
                {category.icon}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-6 gap-4">
            {activeCategory.emojis.map((emoji, index) => (
              <button
                key={`${emoji}-${index}`}
                type="button"
                onClick={() => handleEmojiClick(emoji)}
                className="flex h-44 w-44 items-center justify-center rounded-8 text-24 hover:bg-grey-10"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export { EmojiPicker }
